import React from 'react';

import PropTypes from 'prop-types';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import { connect, useDispatch } from 'react-redux';

import UserSessionDataHandler from 'auth/UserSessionsDataHandler';
import { addAlert } from 'store/actions/alert/alerts';
import { addLayerToMap, removeComponentLayers } from 'utils/map/map';
import { addZonesFeaturesToLayer } from 'components/zones/utils/zoneDetailsUtils';
import { authPatchRequestWithParam } from 'helpers/apiRequests';
import { createLayer, removeLayerIfExists } from 'utils/map/layers';
import { hasObjectKeys } from 'utils/main';
import { addApprovedAreasToLayer } from 'components/approvedAreas/utils/main';
import { Grid } from '@material-ui/core';
import { formStyles } from 'consts/styles/Form';
import { getDashboardConfig, getUserUidFromStorage } from './utils/main';

const DashboardConfiguration = props => {
  const { language, map, allLocalZones, approvedAreas } = props;
  const dispatch = useDispatch();
  const zoneLayer = React.useRef(null);
  const approvedAreasLayer = React.useRef(null);
  const [activeSources, setActiveSources] = React.useState(
    getDashboardConfig()?.zones || []
  );
  const [showApprovedAreas, setShowApprovedAreas] = React.useState(
    UserSessionDataHandler.getStateOfDashboardApprovedAreas()
  );

  const formClasses = formStyles();

  React.useEffect(() => {
    if (!hasObjectKeys(map)) return;
    zoneLayer.current = createLayer('zoneLayer');
    addLayerToMap(map, zoneLayer.current);

    return () => removeComponentLayers(map);
  }, [map]);

  const handleShowApprovedAreas = () => {
    approvedAreasLayer.current = createLayer('approvedAreasLayer');
    addApprovedAreasToLayer(approvedAreasLayer.current, approvedAreas);
    addLayerToMap(map, approvedAreasLayer.current);
  };

  const handleHideApprovedAreas = () => {
    removeLayerIfExists(map, approvedAreasLayer.current);
    approvedAreasLayer.current = null;
  };

  React.useEffect(() => {
    addZonesFeaturesToLayer(zoneLayer.current, getOnlyFilteredZones());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSources]);

  React.useEffect(() => {
    if (showApprovedAreas) handleShowApprovedAreas();
    else handleHideApprovedAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showApprovedAreas]);

  const getOnlyFilteredZones = () => {
    const onlyNeededZones = {};
    activeSources.forEach(filterName => {
      if (
        allLocalZones[filterName] !== undefined &&
        Object.keys(allLocalZones[filterName]).length > 0
      )
        onlyNeededZones[filterName] = allLocalZones[filterName];
    });

    return onlyNeededZones;
  };

  const updateUserConfiguration = updatedConfigData => {
    authPatchRequestWithParam('setUserConfiguration', getUserUidFromStorage(), {
      configuration: updatedConfigData,
    }).then(result => {
      if (result.status === 200)
        UserSessionDataHandler.saveToUserSettingsObjectInStorage(
          UserSessionDataHandler.defaultDashboard,
          result.data.configuration.dashboard
        );
      else
        dispatch(
          addAlert({
            title: language.alerts[result.status.code].title_,
            desc: language.alerts[result.status.code].desc_,
            type: 'error',
          })
        );
    });
  };

  const handleSwitchChange = event => {
    const oldActiveSources = [...activeSources];
    const index = oldActiveSources.indexOf(event.target.name);
    if (index >= 0) oldActiveSources.splice(index, 1);
    else oldActiveSources.push(event.target.name);

    setActiveSources(oldActiveSources);
    const configData = UserSessionDataHandler.getUserConfig();
    const updatedConfigData = {
      ...configData,
      dashboard: {
        ...configData.dashboard,
        zones: oldActiveSources,
      },
    };
    updateUserConfiguration(updatedConfigData);
  };

  const handleApprovedAreasSwitchChange = () => {
    UserSessionDataHandler.saveStateOfDashboardApprovedAreas(
      !showApprovedAreas
    );
    setShowApprovedAreas(!showApprovedAreas);
    updateUserConfiguration(UserSessionDataHandler.getUserConfig());
  };

  const isSourceSelected = source => !!activeSources.includes(source);

  const zonesSwitches = Object.keys(allLocalZones).map((source, index) => (
    <Grid item xs={3} key={index}>
      <Switch
        className="switch"
        classes={{
          checked: `${formClasses.Form}__switch`,
          track: `${formClasses.Form}__switch__track`,
        }}
        checked={isSourceSelected(source)}
        name={source}
        onChange={handleSwitchChange}
        inputProps={{ 'aria-label': source }}
      />
      <span className={`${formClasses.Form}__text-field`}>
        {source.length > 6 && source.substring(0, source.indexOf('_'))}
      </span>
    </Grid>
  ));

  const ApprovedAreasSwitch = (
    <Grid item xs={3} key="approvedAreas">
      <Switch
        color="primary"
        className="switch"
        classes={{
          checked: `${formClasses.Form}__switch`,
          track: `${formClasses.Form}__switch__track`,
        }}
        checked={showApprovedAreas}
        name="approvedAreas"
        onChange={handleApprovedAreasSwitchChange}
        inputProps={{ 'aria-label': 'approved areas' }}
      />
      <span className={`${formClasses.Form}__text-field `}>
        {language.dashboardConfig.zones.approvedAreas_}
      </span>
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography
          variant="h3"
          align="center"
          className={`${formClasses.Form}__text-field ${formClasses.Form}__text-field__title`}
        >
          {language.dashboardConfig.zones.title_}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={3}>
          {zonesSwitches?.length > 0
            ? zonesSwitches
            : language.dashboardConfig.zones.noData_}
          {ApprovedAreasSwitch}
        </Grid>
      </Grid>
    </Grid>
  );
};

DashboardConfiguration.propTypes = {
  language: PropTypes.object.isRequired,
  map: PropTypes.object.isRequired,
  allLocalZones: PropTypes.object.isRequired,
  approvedAreas: PropTypes.array.isRequired,
};

const mapStateToProps = state => ({
  language: state.language,
  map: state.map,
  allLocalZones: state.allLocalZones,
  approvedAreas: state.approvedAreas,
});

export default connect(mapStateToProps)(DashboardConfiguration);
